import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { Menu, X, User, LogOut, LayoutDashboard, Grid, Settings, Briefcase, MessageCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useSettingsStore } from '@/store/settingsStore';
import { NotificationsDropdown } from '@/components/notifications/NotificationsDropdown';

export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuthStore();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const { utilityMode, toggleUtilityMode } = useSettingsStore();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showProfileMenu && !target.closest('.profile-menu-container')) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  // Reset profile menu state when user changes or logs out
  useEffect(() => {
    setShowProfileMenu(false);
  }, [user?.id, isAuthenticated]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  const isProvider = user?.role === 'provider' || user?.role === 'company';
  const isAdmin = user?.role === 'admin';

  // Get the correct settings route based on user role
  const getSettingsRoute = () => {
    if (!user) return '/settings';
    if (user.role === 'provider' || user.role === 'company') {
      return '/settings/provider';
    }
    return '/settings';
  };

  // Get the correct profile route based on user role and provider type
  const getProfileRoute = () => {
    if (!user) return '/profile/customer';
    if (user.role === 'customer') return '/profile/customer';
    if (user.role === 'provider' || user.role === 'company') {
      return user.providerType?.toLowerCase() === 'company' ? '/profile/company' : '/profile/individual';
    }
    return '/profile/customer'; // Default for admin or other roles
  };

  return (
    <>
      {/* Skip Links for Accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>
      <a 
        href="#navigation" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-40 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to navigation
      </a>
      
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm py-2'
          : 'bg-white border-b border-gray-100 py-3'
          }`}
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">

          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              <div className="w-10 h-10 bg-primary-600 flex items-center justify-center rounded-lg shadow-sm group-hover:shadow-md transition-all">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div className="ml-3 flex flex-col">
                <span className="text-xl font-bold text-gray-900">
                  Rapid<span className="text-primary-600">Fix</span>
                </span>
                <span className="text-xs text-gray-500">Service Marketplace</span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav 
            id="navigation" 
            className="hidden md:flex items-center space-x-1"
            role="navigation"
            aria-label="Main navigation"
          >
            {isAuthenticated && (
              <>
                <NavLink to="/dashboard" icon={LayoutDashboard} active={location.pathname === '/dashboard'}>
                  {isAdmin ? 'Admin' : 'Dashboard'}
                </NavLink>
                {isProvider && (
                  <NavLink to="/provider-jobs" icon={Grid} active={location.pathname === '/provider-jobs'}>
                    Jobs
                  </NavLink>
                )}
                {!isProvider && !isAdmin && (
                  <NavLink to="/customer-jobs" icon={Grid} active={location.pathname === '/customer-jobs'}>
                    My Jobs
                  </NavLink>
                )}
                <NavLink to="/chat" icon={MessageCircle} active={location.pathname === '/chat'}>
                  Messages
                </NavLink>
              </>
            )}
          </nav>

          {/* Right Profile / Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <NotificationsDropdown />

                <div className="relative profile-menu-container">
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center gap-3 px-3 py-2 border border-gray-200 bg-white hover:border-primary-300 rounded-lg transition-all min-h-touch"
                    aria-label="Open profile menu"
                    aria-expanded={showProfileMenu}
                    aria-haspopup="true"
                  >
                    <div className="w-8 h-8 bg-gray-100 border border-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                      {user?.avatar_url || user?.profileImage ? (
                        <img src={user.avatar_url || user.profileImage} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-4 h-4 text-gray-600" />
                      )}
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-sm font-semibold text-gray-900">
                        {user?.first_name || user?.firstName}
                      </span>
                      <span className="text-xs text-gray-500 capitalize">
                        {user?.role ?? ''}
                      </span>
                    </div>
                  </button>

                  {/* Dropdown Menu */}
                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50">
                      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                        <p className="text-xs text-gray-500 mb-1">Signed in as</p>
                        <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
                      </div>
                      <div className="py-2">
                        <MenuLink to={getProfileRoute()} icon={User} onClick={() => setShowProfileMenu(false)}>Profile</MenuLink>
                        <MenuLink to={getSettingsRoute()} icon={Settings} onClick={() => setShowProfileMenu(false)}>Settings</MenuLink>
                        <div className="border-t border-gray-100 my-2"></div>
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors min-h-touch"
                          aria-label="Sign out of your account"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">Sign In</Link>
                <Link to="/signup" className="px-4 py-2 bg-primary-600 text-white font-medium text-sm rounded-lg hover:bg-primary-700 transition-all">Sign Up</Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            {isAuthenticated && <NotificationsDropdown />}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg min-h-touch min-w-touch"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div 
          id="mobile-menu" 
          className="md:hidden bg-white border-b border-gray-200 py-4 px-4 sm:px-6"
          role="navigation"
          aria-label="Mobile menu"
        >
          <div className="space-y-2">
            {!isAuthenticated ? (
              <div className="flex flex-col gap-2">
                <Link to="/login" className="w-full py-3 border border-gray-300 text-gray-700 text-center font-medium text-sm rounded-lg" onClick={() => setIsMenuOpen(false)}>Sign In</Link>
                <Link to="/signup" className="w-full py-3 bg-primary-600 text-white text-center font-medium text-sm rounded-lg" onClick={() => setIsMenuOpen(false)}>Sign Up</Link>
              </div>
            ) : (
              <>
                <NavLink to="/dashboard" onClick={() => setIsMenuOpen(false)}>Dashboard</NavLink>
                <NavLink to={getProfileRoute()} onClick={() => setIsMenuOpen(false)}>Profile</NavLink>
                <NavLink to={getSettingsRoute()} onClick={() => setIsMenuOpen(false)}>Settings</NavLink>
                <button onClick={handleLogout} className="w-full text-left px-4 py-3 text-red-600 font-medium text-sm border-t border-gray-200 mt-2">
                  Sign Out
                </button>
              </>
            )}
          </div>
        </div>
      )}
      </header>
    </>
  );
}

function NavLink({ to, children, icon: Icon, active, onClick }: { to: string, children: React.ReactNode, icon?: any, active?: boolean, onClick?: () => void }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${active
        ? 'text-primary-600 bg-primary-50'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
        }`}
    >
      {Icon && <Icon className={`w-4 h-4 ${active ? 'text-primary-600' : 'text-gray-500'}`} />}
      {children}
    </Link>
  );
}

function MenuLink({ to, children, icon: Icon, onClick }: any) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
    >
      {Icon && <Icon className="w-4 h-4 text-gray-500" />}
      {children}
    </Link>
  );
}
